// Steps, acts and answer options for /welcome. The itemIds match the
// reference tables (data/*.json and the backend's lookups); the labels are
// the short onboarding versions.

export const ACCOUNT_STEPS = ["name", "email", "verify", "secure"]
export const PROFILE_STEPS = [
  "intent",
  "idea",
  "startup",
  "bring",
  "need",
  "where",
  "photo",
  "bio",
]
// "plan" comes after completeOnboarding, so it isn't a saved profile step
export const ALL_STEPS = [...ACCOUNT_STEPS, ...PROFILE_STEPS, "plan", "reveal"]

export const ACTS = ["You", "Building", "Who you need", "Profile", "Matches"]
export const STEP_ACT = {
  name: 0,
  email: 0,
  verify: 0,
  secure: 0,
  intent: 1,
  idea: 1,
  startup: 1,
  bring: 2,
  need: 2,
  where: 2,
  photo: 3,
  bio: 3,
  plan: 4,
  reveal: 4,
}

export const SKILLS = [
  { itemId: 1, key: "dev", label: "Developer", sub: "Builds the product" },
  { itemId: 2, key: "design", label: "Designer", sub: "Shapes how it looks and works" },
  { itemId: 3, key: "mkt", label: "Marketer", sub: "Gets it in front of people" },
  { itemId: 4, key: "biz", label: "Business development", sub: "Partnerships and deals" },
  { itemId: 5, key: "sales", label: "Sales", sub: "Turns interest into revenue" },
  { itemId: 6, key: "product", label: "Product", sub: "Decides what to build next" },
]

export const IDEA_STAGES = [
  { itemId: 1, label: "Validating" },
  { itemId: 2, label: "Planning" },
  { itemId: 3, label: "Building" },
  { itemId: 4, label: "Launched" },
]

export const STARTUP_STAGES = [
  { itemId: 1, label: "New" },
  { itemId: 2, label: "Growing" },
  { itemId: 3, label: "Established" },
  { itemId: 4, label: "Exited" },
]

export const INDUSTRIES = [
  { itemId: 1, label: "Fintech" },
  { itemId: 2, label: "Retail" },
  { itemId: 3, label: "E-commerce" },
  { itemId: 4, label: "Construction & trades" },
  { itemId: 5, label: "Digital agency" },
  { itemId: 6, label: "Other agency" },
  { itemId: 7, label: "Consulting" },
  { itemId: 8, label: "Manufacturing" },
  { itemId: 9, label: "Wholesale" },
  { itemId: 10, label: "Web or mobile app" },
  { itemId: 11, label: "Content website" },
  { itemId: 12, label: "Other" },
]

export const HOURS = [
  { itemId: 1, label: "0–10 hrs", sub: "Side project" },
  { itemId: 2, label: "10–20 hrs", sub: "Nights & weekends" },
  { itemId: 3, label: "20–30 hrs", sub: "Serious about it" },
  { itemId: 4, label: "30+ hrs", sub: "All in" },
]

export const INTENTS = [
  {
    key: "idea",
    field: "hasIdeaForStartup",
    label: "I have an idea",
    sub: "and I need a co-founder to build it with",
  },
  {
    key: "join",
    field: "lookingToJoinStartupAsFounder",
    label: "I want to join an idea",
    sub: "as a co-founder on someone else’s startup",
  },
  {
    key: "startup",
    field: "hasOwnStartup",
    label: "I already run a startup",
    sub: "and I want the right person to grow it with",
  },
  {
    key: "jobs",
    field: "interestedInJobsForStartups",
    label: "I’m open to startup jobs",
    sub: "I’d like to hear about roles at early-stage teams",
  },
]

export const REMOTE_LOCATION = "Remote"

export const labelOf = (list, itemId) => {
  const found = list.find((x) => x.itemId === itemId)
  return found ? found.label : null
}

export const EMPTY_ANSWERS = {
  firstName: "",
  email: "",
  ticket: null,
  ticketExpiresAt: null,
  lastName: "",
  intents: { idea: false, join: false, startup: false, jobs: false },
  ideaTitle: "",
  ideaStage: null,
  ideaIndustry: null,
  ideaPitch: "",
  startupStage: null,
  startupIndustry: null,
  startupUrl: "",
  bring: [],
  need: [],
  city: null, // { label, lat, lng }
  remote: false,
  hours: null,
  photo: null, // stored URL
  bio: "",
}

// profile steps this user will see, given their intents
export const visibleProfileSteps = (answers) =>
  PROFILE_STEPS.filter(
    (id) =>
      (id !== "idea" || answers.intents.idea) &&
      (id !== "startup" || answers.intents.startup)
  )

// Answers rebuilt from the saved profile (resume after a refresh).
export const answersFromMe = (me) => {
  const draftIdea = (me.ideas || []).find((idea) => !idea.isActive) || null
  const location = me.location || null
  return {
    ...EMPTY_ANSWERS,
    firstName: me.firstName || "",
    lastName: me.lastName || "",
    email: me.email || "",
    intents: {
      idea: Boolean(me.hasIdeaForStartup),
      join: Boolean(me.lookingToJoinStartupAsFounder),
      startup: Boolean(me.hasOwnStartup),
      jobs: Boolean(me.interestedInJobsForStartups),
    },
    ideaTitle: draftIdea ? draftIdea.title : "",
    ideaStage: me.ideaStage ? me.ideaStage.itemId : null,
    ideaIndustry: me.ideaIndustry ? me.ideaIndustry.itemId : null,
    ideaPitch: me.elevatorPitchForStartup || "",
    startupStage: me.startupStage ? me.startupStage.itemId : null,
    startupIndustry: me.startupIndustry ? me.startupIndustry.itemId : null,
    startupUrl: (me.ownStartupUrl || "").replace(/^https?:\/\//i, ""),
    bring: (me.hasSkills || []).map((s) => s.itemId),
    need: (me.wantsSkills || []).map((s) => s.itemId),
    city:
      location && location !== REMOTE_LOCATION
        ? { label: location, lat: me.locationLat, lng: me.locationLng }
        : null,
    remote: location === REMOTE_LOCATION,
    hours: me.hoursPerWeekAvailable ? me.hoursPerWeekAvailable.itemId : null,
    photo: me.profilePic || null,
    bio: me.whyMakeGoodCofounder || "",
  }
}

// The plan step is done once they're on Premium or chose Free. Choosing
// Premium alone isn't enough: the checkout may have been cancelled.
export const planDone = (me) => Boolean(me.hasValidCoFounderSubscription || me.onboardingPlan === "free")

// Where a signed-in user picks up: the step after the last one they saved.
export const resumeStep = (me, answers) => {
  if (me.onboardingCompletedAt) return planDone(me) ? "reveal" : "plan"
  const saved = me.onboardingStep
  const steps = visibleProfileSteps(answers)
  const i = steps.indexOf(saved)
  if (!saved || i === -1) return "intent"
  return steps[Math.min(i + 1, steps.length - 1)]
}

// words in a text, for the soft length meters
export const wordCount = (text) => {
  const words = (text || "").trim().match(/\S+/g)
  return words ? words.length : 0
}

// ---- plan step (docs/onboarding-tier-selection-plan.md) ----

// Prices and TRIAL_DAYS live in lib/premium-plans.js.
// Stripe's trial_will_end, 3 days before the trial ends
export const TRIAL_REMINDER_DAY = 4

// Premium features that aren't built yet. Each shows a "Soon" tag until it
// ships; set it to false then.
export const SOON = {
  dailyPlan: true,
  whoViewed: true,
}

// "Compare every feature". true is a tick, false a dash, a string is shown;
// `soon` tags the Premium cell while SOON[key] is true.
export const PLAN_FEATURES = [
  { label: "Browse founders and ideas", free: true, premium: true },
  { label: "Post ideas and questions", free: true, premium: true },
  { label: "New conversations", free: "1 a day", premium: "Unlimited" },
  { label: "Listed in the founder grid", free: false, premium: "Shown first" },
  { label: "Suggested connections", free: "1 a week", premium: "Fresh every day" },
  { label: "FounderAssist plan refreshes", free: "1 a week", premium: "3 a day" },
  { label: "Daily tasks, people and reading", free: false, premium: true, soon: "dailyPlan" },
  { label: "Check-ins and 90-day reviews", free: "With each refresh", premium: "With each refresh" },
  { label: "Read receipts", free: false, premium: true },
  { label: "See who viewed you", free: false, premium: true, soon: "whoViewed" },
  { label: "Advanced search filters", free: false, premium: true },
  { label: "Partner discounts", free: false, premium: true },
]

// who the match count is made of, by skill itemId
export const SKILL_PLURALS = {
  1: "Developers",
  2: "Designers",
  3: "Marketers",
  4: "Business developers",
  5: "Salespeople",
  6: "Product people",
}
export const SKILL_SINGULARS = {
  1: "a developer",
  2: "a designer",
  3: "a marketer",
  4: "a business developer",
  5: "a salesperson",
  6: "a product person",
}
